<template lang="pug">
  CrmKanbanUi(
    :stages="columns"
    :leads="viewLeads"
    @moveLead="onLeadMove"
    @removeLead="removeLead"
    @openLead="$emit('openLead', $event.id)"
    )
</template>

<script>
import crmModel from '@/model/crm';
import popupModel from '@/model/popup';
import utils from '@/utils/common';

import crmLeadApi from '@/api/crmLead';

import CrmKanbanUi from '@/components/crm/CrmKanbanUi.vue';

export default {
  name: 'CrmKanbanApi',
  components: { CrmKanbanUi },
  props: {
    // видимые колонки доски: этапы воронки + закрывающие (см. CrmPage)
    columns: {
      type: Array,
      required: true,
    },
    // заявки, уже отфильтрованные и отсортированные страницей
    leads: {
      type: Array,
      required: true,
    },
    // причины отказа воронки — нужны при переносе в колонку «Отказ»
    reasons: {
      type: Array,
      default: () => [],
    },
    // владелец компании или заместитель: удаляет любую заявку воронки
    canManage: Boolean,
  },
  computed: {
    /**
     * Карточка канбана: CrmKanbanUi — презентационный компонент и о формате
     * заявки из API ничего не знает.
     */
    viewLeads() {
      return this.leads.map(lead => ({
        id: lead.id,
        stageId: crmModel.leadColumnKey(lead),
        date: utils.convertDateToString(lead.createdAt, 'numeric', true),
        name: lead.client?.name ?? '',
        phone: lead.client?.phone ?? '',
        description: lead.title,
        projects: (lead.projects ?? []).map(project => ({ id: project.id, name: project.name })),
        // причина есть только у отказной заявки: при возврате в работу сервер
        // её гасит (см. reopenLead)
        rejectionReason: lead.rejectionReason?.name ?? null,
        canRemove: this.canRemoveLead(lead),
        notificationCount: this.notificationCountByLead[lead.id] ?? 0,
      }));
    },
    currentUserIri() { return this.$store.state.auth.user?.['@id'] ?? null; },
    /** Непрочитанные напоминания по заявкам — как countByTask для задач. */
    notificationCountByLead() { return this.$store.getters['notification/countByLead']; },
  },
  methods: {
    /**
     * Удалить заявку могут владелец компании и заместитель — любую, а участник
     * воронки — только ту, где менеджер он сам. Заявка без менеджера остаётся
     * за руководящим составом (п. 2.6 описания прав).
     */
    canRemoveLead(lead) {
      if (this.canManage) return true;

      return !!lead.managerIri && lead.managerIri === this.currentUserIri;
    },
    /** row — карточка из вёрстки, поэтому саму заявку ищем по id. */
    async removeLead(row) {
      const lead = this.leads.find(item => item.id === row.id);
      if (!lead) return;

      const confirmed = await popupModel.addConfirm(
        'Удалить заявку?',
        lead.title,
      );
      if (!confirmed) return;

      const result = await crmModel.run(
        () => crmLeadApi.delete(lead.id),
        'Не удалось удалить заявку',
      );
      if (result === null) return;

      this.$emit('leadRemoved', lead.id);
    },
    /**
     * Карточку перекладываем в новую колонку сразу, до запроса, и откатываем,
     * если он не прошёл.
     *
     * Ждать ответа тут нельзя: DraggableList по отпусканию мыши сразу снимает
     * своё превью и доска снова рисуется строго по пропу leads — то есть на
     * всё время PATCH карточка возвращалась бы в исходную колонку и только
     * потом переезжала в новую.
     */
    async onLeadMove({ leadId, stageId }) {
      const lead = this.leads.find(l => l.id === leadId);
      const column = this.columns.find(c => c.id === stageId);
      if (!lead || !column) return;

      // Причину отказа спрашиваем до переноса: пока попап открыт, карточка
      // должна оставаться на месте, а отказ отвечать — не двигать её вовсе
      let rejectionReason = null;
      if (column.closing && column.isSuccessful === false) {
        rejectionReason = await crmModel.askRejectionReason(this.reasons);
        // без причины бэк вернёт 422, так что заявку не трогаем
        if (!rejectionReason) return;
      }

      this.$emit('leadChanged', crmModel.leadAfterMove(lead, column, rejectionReason));

      const updated = await crmModel.applyLeadColumn(lead, column, rejectionReason);
      // неудача — возвращаем карточку туда, откуда её взяли
      this.$emit('leadChanged', updated ?? lead);
    },
  },
};
</script>
